import { isPlainObject } from "./typeGuards";

/** `Object.keys` that preserves the key type instead of widening to `string`. */
export function objectKeys<T extends object>(obj: T): Array<keyof T> {
    return Object.keys(obj) as Array<keyof T>;
}

export function pick<T extends object, K extends keyof T>(
    obj: T,
    keys: readonly K[],
): Pick<T, K>;
export function pick<T extends object, K extends keyof T>(
    obj: T,
    ...keys: K[]
): Pick<T, K>;
export function pick<T extends object>(obj: T, ...args: any[]): any {
    const keys: PropertyKey[] = Array.isArray(args[0]) ? [...args[0]] : args;
    const result: Partial<T> = {};
    for (const key of keys) {
        if (key in obj) {
            (result as any)[key] = (obj as any)[key];
        }
    }
    return result;
}

export function mapKeys<T extends Record<string, unknown>>(
    obj: T,
    fn: (value: T[keyof T], key: string) => string,
): Record<string, T[keyof T]> {
    const result: Record<string, T[keyof T]> = {};
    for (const [key, value] of Object.entries(obj)) {
        result[fn(value as T[keyof T], key)] = value as T[keyof T];
    }
    return result;
}

export function omit<T extends object, K extends keyof T>(
    obj: T,
    keys: K[],
): Omit<T, K>;
export function omit<T extends object, K extends keyof T>(
    obj: T,
    ...keys: K[]
): Omit<T, K>;
export function omit<T extends object>(obj: T, ...args: any[]): any {
    const keys: PropertyKey[] = Array.isArray(args[0]) ? args[0] : args;
    const result = { ...obj };
    for (const key of keys) {
        delete (result as any)[key];
    }
    return result;
}

export function omitBy<T extends Record<string, unknown>>(
    obj: T,
    predicate: (value: T[keyof T], key: string) => boolean,
): T {
    const result: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(obj)) {
        if (!predicate(value as T[keyof T], key)) {
            result[key] = value;
        }
    }
    return result as T;
}

export function pickBy<T extends Record<string, unknown>>(
    obj: T,
    predicate: (value: T[keyof T], key: string) => boolean,
): Partial<T> {
    const result: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(obj)) {
        if (predicate(value as T[keyof T], key)) {
            result[key] = value;
        }
    }
    return result as Partial<T>;
}

export function mapValues<T extends Record<PropertyKey, any>, K extends string>(
    obj: T,
    key: K,
): Record<string, T[keyof T] extends Record<K, infer V> ? V : unknown>;
export function mapValues<T extends Record<PropertyKey, unknown>, V>(
    obj: T,
    fn: (value: T[keyof T], key: string) => V,
): Record<string, V>;
export function mapValues(
    obj: Record<PropertyKey, any>,
    fnOrKey: ((value: any, key: string) => any) | string,
): Record<string, any> {
    const result: Record<string, any> = {};
    for (const [key, value] of Object.entries(obj)) {
        result[key] =
            typeof fnOrKey === "function"
                ? fnOrKey(value, key)
                : value?.[fnOrKey];
    }
    return result;
}

export function deepMerge<T extends Record<string, any>>(
    target: T,
    ...sources: Array<Record<string, any>>
): T {
    for (const source of sources) {
        for (const key of Object.keys(source)) {
            const targetVal = (target as Record<string, unknown>)[key];
            const sourceVal = source[key];
            if (isPlainObject(targetVal) && isPlainObject(sourceVal)) {
                (target as Record<string, unknown>)[key] = deepMerge(
                    { ...targetVal },
                    sourceVal,
                );
            } else {
                (target as Record<string, unknown>)[key] = sourceVal;
            }
        }
    }
    return target;
}
